import { useEffect, useRef } from 'react'

const EmbeddableChatWidget = ({
  botId, // Bot UUID from API
  botToken, // Bot secret key from API
  position = 'bottom-right',
  theme = 'dark',
  themeStyle = 'solid',
  primaryColor = '#3b82f6',
  placeholder = "Type your message...",
  zIndex = 9999,
  autoOpen = false,
  showWelcomeMessage = false,
  welcomeMessage = "Hello! How can I help you today?",
  chatbotAvatar = '🤖',
  customAvatar = '',
  botName = 'Talkida Technologies'
}) => {
  const scriptRef = useRef(null)

  // Load CDN script when component mounts
  useEffect(() => {

    // Remove existing script if it exists and is still in the DOM
    if (scriptRef.current && document.head.contains(scriptRef.current)) {
      document.head.removeChild(scriptRef.current)
    }

    // Create new script element
    const script = document.createElement('script')
    script.src = 'https://cdn.talkida.co.in/widget/talkida-widget.js'
    // The CDN script supports either bot-id/bot-token OR data-api-key/data-chatbot-id.
    // We prefer the explicit CDN attributes first.
    script.setAttribute('bot-id', botId || '')
    script.setAttribute('bot-token', botToken || '')
    script.setAttribute('data-position', position)
    script.setAttribute('data-theme', theme)
    script.setAttribute('data-theme-style', themeStyle)
    script.setAttribute('data-primary-color', primaryColor)
    script.setAttribute('data-welcome-message', welcomeMessage)
    script.setAttribute('data-placeholder', placeholder)
    script.setAttribute('data-show-welcome', 'false')
    script.setAttribute('data-auto-open', autoOpen.toString())
    script.setAttribute('data-z-index', zIndex.toString())
    script.setAttribute('data-chatbot-avatar', chatbotAvatar)
    script.setAttribute('data-custom-avatar', customAvatar)
    script.setAttribute('data-bot-name', botName)
    script.defer = true
    script.type = 'text/javascript'

    // Add event listeners for debugging
    script.onload = () => {
      // If DOMContentLoaded has already fired (SPA), manually initialize
      try {
        const hasInstance = typeof window !== 'undefined' && window.talkidaInstance
        if (!hasInstance && typeof window !== 'undefined' && window.TalkidaWidget) {
          const cfg = {
            botId,
            botToken,
            botUuid: botId,
            botSecret: botToken,
            position,
            theme,
            themeStyle,
            primaryColor,
            welcomeMessage,
            placeholder,
            showWelcomeMessage: false,
            autoOpen,
            zIndex,
            chatbotAvatar,
            customAvatar,
            botName
          }
          window.talkidaInstance = new window.TalkidaWidget(cfg)
        }
      } catch (e) {
        console.warn('EmbeddableChatWidget: Manual init failed', e)
      }

      // Check if widget was created after a short delay
      setTimeout(() => {
        const widgetElements = document.querySelectorAll('[data-talkida-widget]')

        if (widgetElements.length === 0) {
          const chatElements = document.querySelectorAll('[class*="chat"], [class*="widget"], [id*="chat"], [id*="widget"]')
        }
      }, 1000)
    }
    script.onerror = (error) => {
      console.error('EmbeddableChatWidget: Failed to load CDN script:', error)
    }

    // Store reference for cleanup
    scriptRef.current = script

    // Add script to document head
    document.head.appendChild(script)

    // Cleanup function
    return () => {
      // Destroy existing widget instance if present
      try {
        if (typeof window !== 'undefined' && window.talkidaInstance && typeof window.talkidaInstance.destroy === 'function') {
          window.talkidaInstance.destroy()
          window.talkidaInstance = null
        }
      } catch (e) {
        console.warn('EmbeddableChatWidget: Widget destroy warning', e)
      }

      if (scriptRef.current && document.head.contains(scriptRef.current)) {
        try {
          document.head.removeChild(scriptRef.current)
        } catch (error) {
          // Script might have been removed already, ignore the error
          console.warn('Script cleanup warning:', error.message)
        }
      }
    }
  }, [botId, botToken, position, theme, themeStyle, primaryColor, placeholder, zIndex, autoOpen, showWelcomeMessage, welcomeMessage, chatbotAvatar, customAvatar, botName])

  // The CDN script handles all the UI rendering
  // This component just loads the script with the correct parameters
  return null
}

export default EmbeddableChatWidget
